// The api-injection seam — how the shared miniapp gets its data
// client WITHOUT importing a platform singleton.
//
// THE PROBLEM this closes: the miniapp hooks (useChannel, usePresence, …) used
// to `import { subscribeChat, … } from "../api/client"` — a web-app-relative
// singleton wired to localStorage + VITE_API_URL + PostHog. That import is what
// welded the miniapp to apps/web; a desktop app couldn't reuse it.
//
// THE FIX: `WaytideApi` is the EXACT set of bound methods the miniapp calls —
// nothing aspirational (it's a consumer contract, not "the API
// surface"). Each app builds its own client from `@waytide/core`
// (`createClient`) wired to its own platform seam (web → localStorage + PostHog;
// desktop → its own token store), then provides it ONCE via <ApiProvider>. The
// shared hooks read it via `useApi()`. So web and desktop share the UI verbatim
// and inject the platform bits at the edge — and cannot drift on the contract,
// because both satisfy the same `WaytideApi`.

import { createContext, useContext, type ReactNode } from "react";
import type {
  BrainEvent,
  ChatMessage,
  FocusState,
  PreferencesResponse,
  PresenceResponse,
  ProjectQuestionsResponse,
  ProjectWikiResponse,
  PauseResponse,
  SchematicResponse,
  SessionPresence,
  StreamHealth,
  Thread,
  TraceEvents,
  TraceStreamEvent,
} from "@waytide/core";

/** The exact data-client contract the shared miniapp consumes. Each app's
 * `api/client` already exposes these as standalone functions; an app wraps them
 * into this object and provides it. KEEP THIS MINIMAL — if the miniapp stops
 * calling a method, it leaves this interface (it is the used set, not the whole
 * client). The dashboard-only methods (projects, hours, rules, radio, …) are NOT
 * here; they stay in apps/web's client and never cross into the shared layer. */
export interface WaytideApi {
  /** Live chat SSE: message / thread / presence / trace / preferences / brain /
   * health callbacks, auto-reconnecting; returns an unsubscribe. The miniapp
   * uses the message/trace/presence/brain/health lanes. */
  subscribeChat: (
    onMessage: (m: ChatMessage) => void,
    onThread: (t: Thread) => void,
    onPresence?: (p: SessionPresence) => void,
    onTrace?: (t: TraceStreamEvent) => void,
    onPreferences?: (p: PreferencesResponse) => void,
    onBrain?: (b: BrainEvent) => void,
    onHealth?: (h: StreamHealth) => void,
  ) => () => void;

  /** Every presence row for the caller's threads (the live status spine). */
  getPresence: () => Promise<PresenceResponse>;

  /** A thread's messages, oldest→newest. */
  listThreadMessages: (
    threadId: number,
  ) => Promise<{ messages: ChatMessage[] }>;

  /** A session's Trace (the activity feed). `project` is the thread id string. */
  getTrace: (project: string) => Promise<TraceEvents>;

  /** Send a message; forwards the idempotency key for the durable outbox. */
  sendThreadMessage: (
    threadId: number,
    body: string,
    aside?: boolean,
    idempotencyKey?: string,
  ) => Promise<ChatMessage>;

  /** Pause / resume the agent (the optimistic brake's confirmed wire). */
  pauseSession: (threadId: number) => Promise<PauseResponse>;
  resumeSession: (threadId: number) => Promise<PauseResponse>;

  /** The bound repo's project schematic (the read/write map). */
  getSchematic: (threadId: number) => Promise<SchematicResponse>;

  /** The project's wiki + Signal's async questions (the brain panel). */
  getProjectWiki: (projectId: number) => Promise<ProjectWikiResponse>;
  getProjectQuestions: (
    projectId: number,
  ) => Promise<ProjectQuestionsResponse>;

  /** Report this surface's focus (the per-user presence heartbeat). */
  postFocusHeartbeat: (focus: FocusState) => Promise<void>;
}

const ApiContext = createContext<WaytideApi | null>(null);

/** Provide the platform-wired client to the shared miniapp. Each app mounts this
 * ONCE near its root with its own `api/client` adapted to `WaytideApi`. */
export function ApiProvider({
  api,
  children,
}: {
  api: WaytideApi;
  children: ReactNode;
}) {
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/** Read the injected client. Throws if a shared component is mounted outside an
 * <ApiProvider> — a loud, early failure beats a silent half-wired surface. */
export function useApi(): WaytideApi {
  const api = useContext(ApiContext);
  if (!api) {
    throw new Error(
      "useApi must be used within <ApiProvider> — the app must inject its " +
        "platform-wired WaytideApi at the edge.",
    );
  }
  return api;
}
